<template>
  <div>
    <div>
      <skeleton-wrapper
        id="skeletonWrapper"
        :style="{width: '100%'}"
        :is-calendar="true"
      >
        <template #body>
          <skeleton v-if="isShowSkeleton" />
        </template>
      </skeleton-wrapper>
      <pricing-stub
        v-if="!pricingAccessToCalendar"
        :is-owner="isOwner"
        :plan-title="planTitle"
      />
      <div
        v-else
        id="calendar_view_id"
        ref="calendarView"
        :class="['calendar_view', isTaskSettingsOpened ? 'disabled-layout' : '']"
        :style="styles"
      />
      <new-event-popup
        v-if="isShowNewEventPopup"
        :show="isShowNewEventPopup"
        :date="newEventDate"
        :project-id="projectId"
        @close="isShowNewEventPopup = false"
        @createTask="createNewTask"
      />
      <details-tooltip
        v-if="tooltipConfig"
        :config="tooltipConfig"
      />
    </div>
  </div>
</template>

<script>
import app from '$$app';
import Calendar from './calendar';
import calendarHelpers from './helpers';
import projectsModel from '$$projectModel';
import newEventPopup from './components/newEventPopup/newEventPopup.vue';
import detailsTooltip from './components/detailsTooltip/detailsTooltip.vue';
import _ from 'lodash';
import pricingHelper from '../../helpers/pricingHelper';
import rights from '../../components/rights/index';
import pricingStub from './components/pricingStub/stub.vue';
import skeleton from './components/skeleton/skeleton.vue';
import skeletonWrapper from '../common/VueComponents/skeletons/skeletonWrapper.vue';
import LoadRequiredDataMixin from '../../mixins/LoadRequiredData';

export default {
  name: 'CalendarView',
  components: {
    newEventPopup, detailsTooltip, pricingStub, skeleton, skeletonWrapper,
  },
  mixins: [LoadRequiredDataMixin],
  data() {
    return {
      newEventDate: null,
      tooltipConfig: null,
      isShowNewEventPopup: false,
      isTaskSettingsOpened: false,
      isMounted: false,
      isShowSkeleton: false,
      isNotFoundByFilter: false,
      appEvents: [],
    };
  },
  computed: {
    projectId() {
      return projectsModel.getActiveGanttId();
    },
    route() {
      return this.$route;
    },
    styles() {
      const opacity = this.isMounted ? 1 : 0;

      return { width: '100%', opacity };
    },
    pricingAccessToCalendar() {
      return pricingHelper.checkPricingAccess('calendar_view');
    },
    planTitle() {
      return `${pricingHelper.getPlans('calendar_view')}`;
    },
    isOwner() {
      return rights.account.isOwner();
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    isOpenFilter() {
      return this.$store.getters['filter/isOpen'];
    },
  },
  watch: {
    route() {
      if (this.route.params?.taskId) {
        this.isTaskSettingsOpened = true;
      } else {
        this.isTaskSettingsOpened = false;
      }
    },
    currentFilter(value) {
      this.setCalendarEventsByFilter(value?.properties);
    },
    isOpenFilter(flag) {
      if (!flag && this.isNotFoundByFilter) {
        // app.trigger('filter:reset');
        setTimeout(() => {
          this.$store.dispatch('filter/resetFilter');
        });
      }
    },
    isLoadedRequiredData: {
      handler(val) {
        if (val) {
          this.init();
        }
      },
      immediate: true,
    },
  },
  created() {
    if (!this.pricingAccessToCalendar) {
      return;
    }

    if (calendarHelpers.checkAccessToTasks(this.projectId)) {
      this.isShowSkeleton = true;
    }

    if (this.$route.params?.taskId) {
      this.isTaskSettingsOpened = true;
    }
  },
  beforeUnmount() {
    if (!this.pricingAccessToCalendar) {
      return;
    }

    this.removeDOMListeners();
    this.removeAppListeners();
    Calendar.destroy();
    // this.$refs.calendarView = null;
    this.isMounted = false;
  },
  methods: {
    init() {
      if (!this.pricingAccessToCalendar) {
        return;
      }

      Calendar.init();

      this.initInstanceListeners();
      this.initAppListeners();
      this.initDOMListeners();
      this.setCalendarEventsByFilter(this.currentFilter?.properties);

      setTimeout(() => {
        this.isMounted = true;
        this.updateCalendar();
        this.isShowSkeleton = false;
      }, 3000);
    },
    setCalendarEventsByFilter(filterState) {
      if (!Calendar.calendarInstance) {
        return;
      }

      const projectId = projectsModel.getActiveGanttId();
      const tasks = this.$store.getters['tasksModel/getTasksForGantt'](projectId).data;
      const filteredTasks = Calendar.getTasksByFilterAndAccess(projectId, tasks, filterState);

      if (!filteredTasks.length) {
        if (calendarHelpers.hasAccessToAllTasks(projectId) && !this.isNotFoundByFilter) {
          this.$store.commit('filter/isNotFound', true);
        }

        this.isNotFoundByFilter = true;

        if (!this.isMounted) {
          setTimeout(() => {
            // app.trigger('filter:reset');
            this.$store.dispatch('filter/resetFilter');
          }, 100);

          return;
        }
      } else {
        this.isNotFoundByFilter = false;
      }

      Calendar.parseEvents(filteredTasks);
    },
    checkCalendarRoute() {
      return ['project', 'taskRoute'].includes(this.$route.name) && this.$route.params.mode === 'calendar';
    },
    initDOMListeners() {
      window.addEventListener('click', this.handleClick);
      window.addEventListener('mousedown', this.handleMouseDown);
      window.addEventListener('mouseup', this.handleMouseUp);
      window.addEventListener('wheel', _.throttle(this.hideByScroll, 50));
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('resize', this.onResize);
    },
    removeDOMListeners() {
      window.removeEventListener('click', this.handleClick);
      window.removeEventListener('mousedown', this.handleMouseDown);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('wheel', this.hideByScroll);
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('resize', this.onResize);
    },
    initInstanceListeners() {
      const instance = Calendar.calendarInstance;
      const debouncedMount = _.debounce(Calendar.mount, 0).bind(Calendar);
      const reactiveState = instance.api.getReactiveState();

      instance.api.on('set-bound', obj => {
        debouncedMount();
      });

      instance.api.on('set-date', obj => {
        debouncedMount();
      });

      instance.api.on('add-event', obj => {
        if (obj.taskData) {
          Calendar.tasksMap.set(obj.taskData.id, obj.taskData);
          Calendar.refresh();
        }
      });

      instance.api.on('delete-event', obj => {
        Calendar.tasksMap.delete(obj.id);
        Calendar.closeTaskSettings();

        setTimeout(() => {
          if (!this.isOpenFilter) {
            Calendar.resetFilterIfNeeded(obj.id);
          }
          Calendar.refresh();
        }, 0);
      });

      instance.api.on('update-event', obj => {
        if (obj.id !== obj.event.id) {
          Calendar.replaceEventId(obj.id, obj.event.id);
        }
      });

      instance.api.intercept('update-event', obj => Calendar.checkEventBeforeUpdate(obj));

      instance.api.on('show-new-event-popup', obj => {
        this.newEventDate = obj.date;
        this.isShowNewEventPopup = true;
      });

      instance.api.on('show-details', obj => {
        if (obj) {
          this.tooltipConfig = {
            taskData: obj.task,
            coords: obj.tooltipCoords,
          };
        } else {
          this.tooltipConfig = null;
        }
      });

      instance.api.intercept('set-mode', obj => {
        if (obj) return false;
      });

      instance.api.on('update-events', obj => {
        if (obj.projectId === this.projectId) {
          const filteredTasks = Calendar.getTasksByFilterAndAccess(obj.projectId, obj.tasks, this.currentFilter?.properties);

          Calendar.parseEvents(filteredTasks);
          this.updateCalendar();
        }
      });

      reactiveState.bounds.subscribe(bounds => {
        Calendar.recalculateCalendarHeight(bounds);
      });
    },
    initAppListeners() {
      const customDaysInsert = app.on('customDays:insert', () => {
        if (this.checkCalendarRoute()) {
          this.updateCalendar();
        }
      });

      const customDaysDelete = app.on('customDays:delete', () => {
        if (this.checkCalendarRoute()) {
          this.updateCalendar();
        }
      });

      const updateModels = app.on('collaboration:updatedModels', () => {
        if (this.checkCalendarRoute()) {
          this.updateCalendar();
        }
      });

      const changeWorkTime = app.on('project-calendar:change', () => {
        if (this.checkCalendarRoute()) {
          this.updateCalendar();
        }
      });

      const initModels = app.on('app:initModels', () => {
        if (this.checkCalendarRoute()) {
          this.updateCalendar();
        }
      });

      this.appEvents.push(
        customDaysInsert,
        customDaysDelete,
        initModels,
        updateModels,
        changeWorkTime,
      );
    },
    removeAppListeners() {
      this.appEvents.forEach(event => app.off(event));
      this.appEvents = [];
    },
    handleClick(e) {
      if (!this.isTaskSettingsOpened) {
        Calendar.handleClick(e);
      }
    },
    handleMouseDown(e) {
      if (!this.isTaskSettingsOpened) {
        Calendar.handleMouseDown(e);
      }
    },
    handleMouseUp(e) {
      Calendar.handleMouseUp(e);
    },
    handleMouseMove(e) {
      if (!this.isTaskSettingsOpened) {
        Calendar.handleMouseMove(e);
      }
    },
    createNewTask(newTask, date, resourcesToNewTask) {
      Calendar.createTask(newTask, date, resourcesToNewTask);
    },
    onResize() {
      Calendar.recalculateCalendarHeight();
      Calendar.calcMaxVisibleEvents();
      Calendar.refresh();
    },
    hideByScroll() {
      Calendar.hideDetailsTooltip();
      Calendar.hideHightlights();
    },
    updateCalendar() {
      // hack to artificially trigger an update from the calendar system (needed for render)

      if (Calendar.calendarInstance && 'api' in Calendar.calendarInstance) {
        const state = Calendar.calendarInstance.api.getState();
        const calendar = state.calendars[0];

        Calendar.reInitCalendarConfig();
        Calendar.calendarInstance.api.exec('update-calendar', {
          id: calendar.id,
          calendar: {
            ...calendar,
            key: new Date().getTime(),
          },
        });
      }
    },
  },
};
</script>

<style src='./less/main.less' lang='less'></style>
